import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  SearchRounded as SearchRoundedIcon,
  ClearRounded as ClearRoundedIcon,
  CalculateRounded as CalculateRoundedIcon,
  RefreshRounded as RefreshRoundedIcon,
} from '@mui/icons-material';

const ControlsBar = ({
  searchQuery,
  setSearchQuery,
  activeTab,
  setActiveTab,
  onOpenConverter,
  onRefresh,
  isRefreshing,
  t,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        mb: 3,
      }}
    >
      {/* Category Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        variant={isMobile ? 'fullWidth' : 'standard'}
        sx={{
          minHeight: 44,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(20, 28, 44, 0.7)' : '#FFFFFF',
          p: 0.5,
          borderRadius: 3,
          border: '1px solid',
          borderColor: theme.palette.divider,
          '& .MuiTabs-indicator': {
            height: '100%',
            borderRadius: 2.5,
            backgroundColor: theme.palette.primary.main,
            opacity: 0.15,
          },
          '& .MuiTab-root': {
            minHeight: 40,
            borderRadius: 2.5,
            fontWeight: 700,
            fontSize: '0.875rem',
            textTransform: 'none',
            zIndex: 1,
            transition: 'color 0.2s',
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          },
        }}
      >
        <Tab
          label={
            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, height: '100%' }}>
              <i className="fa-solid fa-border-all" style={{ fontSize: 13, display: 'block', lineHeight: 1 }} />
              <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>{t('allMarkets')}</span>
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, height: '100%' }}>
              <i className="fa-solid fa-money-bill-transfer" style={{ fontSize: 13, display: 'block', lineHeight: 1 }} />
              <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>{t('currencies')}</span>
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, height: '100%' }}>
              <i className="fa-solid fa-coins" style={{ fontSize: 13, color: '#F59E0B', display: 'block', lineHeight: 1 }} />
              <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>{t('goldMetals')}</span>
            </Box>
          }
        />
      </Tabs>

      {/* Right side controls: Search + Converter + Refresh */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t('searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            flexGrow: 1,
            minWidth: { xs: 'auto', sm: 240 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 2.5,
              height: 40,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(20, 28, 44, 0.7)' : '#FFFFFF',
              '& fieldset': {
                borderColor: theme.palette.divider,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearRoundedIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Button
          variant="contained"
          color="primary"
          startIcon={<CalculateRoundedIcon />}
          onClick={onOpenConverter}
          sx={{
            height: 40,
            borderRadius: 2.5,
            px: 2,
            fontWeight: 700,
            fontSize: '0.825rem',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            '& .MuiButton-startIcon': {
              margin: '0 !important',
              display: 'inline-flex !important',
              alignItems: 'center !important',
              justifyContent: 'center !important',
            },
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>{t('converter')}</span>
        </Button>

        <Tooltip title={t('refreshRates')}>
          <IconButton
            onClick={onRefresh}
            disabled={isRefreshing}
            sx={{
              borderRadius: 3,
              p: 1,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(20, 28, 44, 0.7)' : '#FFFFFF',
              border: '1px solid',
              borderColor: theme.palette.divider,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.9)' : '#F1F5F9',
              },
            }}
          >
            <RefreshRoundedIcon
              sx={{
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ControlsBar;
